import { useEffect, useMemo, useReducer, useRef } from "react";
import { coreGet } from "@/services/coreApi.jsx";
import { useRouteStore } from "@/features/route/state/RouteStore.jsx";
import { normalizeCoreWaypoints } from "@/features/route/state/RouteAdapters.jsx";

const initialState = {
  status: "idle",
  requestedRouteIds: [],
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "REQUEST":
      return {
        ...state,
        status: "loading",
        requestedRouteIds: action.payload?.requestedRouteIds ?? [],
        error: null,
      };
    case "SUCCESS":
      return {
        status: "success",
        requestedRouteIds: action.payload?.requestedRouteIds ?? [],
        error: null,
      };
    case "FAIL":
      return {
        ...state,
        status: "error",
        requestedRouteIds: action.payload?.requestedRouteIds ?? [],
        error: action.payload?.error ?? "Unknown error",
      };
    case "CLEAR":
      return initialState;
    default:
      return state;
  }
}

// Pure loader: fetches waypoints for tree-selected routes into the RouteStore
// (the store is the cache). Tracks which ids are loaded so selecting other
// routes doesn't refetch loaded ones; invalidated wholesale on refreshNonce.
export function useTreeRouteWaypoints({
  selectedRouteIds,
  accessContext,
  setAccessContext,
  refreshNonce,
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { replaceRoutes } = useRouteStore();

  const loadedRouteIdsRef = useRef(new Set());
  const nonceRef = useRef(refreshNonce);

  // Content key: identical ids (any order/reference) → same dep, so callers
  // re-setting selectedRouteIds to the same set don't trigger a duplicate fetch.
  const routeIdsKey = useMemo(
    () =>
      Array.from(
        new Set(
          (selectedRouteIds ?? [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id)),
        ),
      )
        .sort((a, b) => a - b)
        .join(","),
    [selectedRouteIds],
  );

  useEffect(() => {
    if (nonceRef.current !== refreshNonce) {
      loadedRouteIdsRef.current = new Set();
      nonceRef.current = refreshNonce;
    }

    const ids = routeIdsKey ? routeIdsKey.split(",").map(Number) : [];

    if (ids.length === 0) {
      loadedRouteIdsRef.current = new Set();
      dispatch({ type: "CLEAR" });
      return;
    }

    const missing = ids.filter((id) => !loadedRouteIdsRef.current.has(id));

    if (missing.length === 0) {
      dispatch({ type: "SUCCESS", payload: { requestedRouteIds: ids } });
      return;
    }

    dispatch({ type: "REQUEST", payload: { requestedRouteIds: ids } });

    let active = true;
    const fetchWaypoints = async () => {
      try {
        const params = new URLSearchParams();
        missing.forEach((id) => params.append("route_ids", String(id)));
        const data = await coreGet(
          `/waypoints?${params.toString()}`,
          accessContext,
          setAccessContext,
          ".core /waypoints (tree selected routes) error=",
        );
        if (!active) return;

        const fetched = Array.isArray(data?.waypoints) ? data.waypoints : [];
        const fragment = normalizeCoreWaypoints(fetched);
        // Requested routes that returned no waypoints must still be emptied
        // in the store — a refetch may legitimately shrink a route to zero.
        for (const id of missing) {
          if (!fragment.waypointsByRouteId[id]) {
            fragment.waypointsByRouteId[id] = [];
          }
          loadedRouteIdsRef.current.add(id);
        }
        replaceRoutes(fragment);

        dispatch({ type: "SUCCESS", payload: { requestedRouteIds: ids } });
      } catch (error) {
        if (!active) return;
        dispatch({
          type: "FAIL",
          payload: {
            requestedRouteIds: ids,
            error: error?.message ?? String(error),
          },
        });
        console.error("Tree waypoint fetch failed:", error);
      }
    };

    fetchWaypoints();
    return () => {
      active = false;
    };
  }, [routeIdsKey, refreshNonce, accessContext, setAccessContext, replaceRoutes]);

  return state;
}
